import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, PlusIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useOTRulesQuery } from "../api/queries";
import { OTRulesTour, useOTRulesTour } from "../tour/OTRulesTour";
import { OTRulesFilter, type OTRulesSearchParams } from "../ui/OTRulesFilter";
import { OTRulesTable } from "../ui/OTRulesTable";

export function OTRulesPage() {
  const nav = useNavigate({ from: "/app/hr/overtime/rules" });
  const search = useSearch({
    from: "/app/hr/overtime/rules",
  }) as OTRulesSearchParams & {
    offset?: number;
    limit?: number;
  };

  const sortBy = search.sort?.[0]?.field ?? "effectiveFrom";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const query = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      dayType: search.dayType,
      isActive: search.isActive,
      search: search.search,
    }),
    [
      search.offset,
      search.limit,
      search.sort,
      search.dayType,
      search.isActive,
      search.search,
    ],
  );

  const listQuery = useOTRulesQuery(query);
  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        dayType: search.dayType,
        isActive: search.isActive,
        search: search.search,
      },
    });
  };

  const handleView = (row: { id: string }) => {
    nav({ to: "/app/hr/overtime/rules/$id", params: { id: row.id } });
  };

  const handleEdit = (row: { id: string }) => {
    nav({ to: "/app/hr/overtime/rules/$id/edit", params: { id: row.id } });
  };

  const handleNewRule = () => {
    nav({ to: "/app/hr/overtime/rules/new" });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          dayType: search.dayType,
          isActive: search.isActive,
          search: search.search,
        },
      });
    },
    [nav, search.limit, search.dayType, search.isActive, search.search],
  );

  const tour = useOTRulesTour();

  return (
    <>
      <Header />
      <Main>
        <OTRulesTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6" data-tourid="ot-rules-toolbar">
          <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ຂໍ້ກຳນົດ OT</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ຈັດການການຕັ້ງຄ່າອັດຕະໂນມັດ OT ຕາມປະເພດວັນທີ່ຕ່າງໆ
                ເພື່ອຄວບຄຸມການຄິດເງິນເດືອນທີ່ຖືກຕ້ອງສໍາລັບພະນັກງານຂອງທ່ານ.
              </p>
            </div>
            <Button onClick={handleNewRule}>
              <PlusIcon className="h-4 w-4" />
              ສ້າງຂໍ້ກຳນົດ OT
            </Button>
          </div>
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="ot-rules-filter"
        >
          <OTRulesFilter
            searchParams={search}
            fromRoute="/app/hr/overtime/rules"
          />
          <div data-tourid="ot-rules-table">
            <OTRulesTable
              data={data}
              isLoading={listQuery.isLoading}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              onPaginationChange={handlePaginationChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortingChange={handleSortingChange}
              onView={handleView}
              onEdit={handleEdit}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
